import { useContext, useState } from "react";
import dayjs from "dayjs";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import {
  LeadingActions,
  SwipeableListItem,
  SwipeAction,
  TrailingActions,
} from "react-swipeable-list";
import { Get } from "../api/api";
import { printTicket } from "../api/printer";
import { GenerateTicket } from "../api/ticket";
import { Button } from "@/components/ui/button";
import { UserContext } from "../context/user";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

function getPaymentIcon(method) {
  if (!method) return "mdi:currency-eur";
  const name = method.toLowerCase();
  if (name.includes("efectivo") || name.includes("metalico")) return "mdi:cash";
  if (name.includes("tarjeta")) return "mdi:credit-card-outline";
  if (name.includes("bizum")) return "mdi:cellphone-nfc";
  if (name.includes("transferencia")) return "mdi:bank-transfer";
  return "mdi:currency-eur";
}

function formatCurrency(value) {
  return `${Number(value || 0).toFixed(2)}€`;
}

export function TicketLine({
  ticket,
  refetch = () => {},
  selected = false,
  onSelect,
}) {
  const [open, setOpen] = useState(false);
  const [returning, setReturning] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [animateRef] = useAutoAnimate();
  const confirm = useConfirmDialog();
  const { shop } = useContext(UserContext);

  const canReturn = !ticket.returned && !ticket.is_returned;
  const isReturned = Boolean(ticket.returned || ticket.is_returned);
  const paperSaving = shop?.ticket_settings?.paper_saving || false;

  function onReturn(event) {
    event?.stopPropagation();
    confirm({
      title: "¿Devolver ticket?",
      description:
        "Esta acción no se puede deshacer. Se generará un ticket de devolución.",
      okText: "Devolver",
      cancelText: "Cancelar",
      variant: "danger",
      onOk: async () => {
        setReturning(true);
        try {
          const refundTicket = await Get({
            request: `/tickets/${ticket.id}/return?shop_id=${localStorage.getItem("shop_id")}`,
          });
          setOpen(false);
          refetch();
          toast.success("Ticket devuelto correctamente");
          printTicket(GenerateTicket(refundTicket, paperSaving));
        } catch {
          toast.error("Error al devolver el ticket");
        } finally {
          setReturning(false);
        }
      },
    });
  }

  async function onPrint(event) {
    event?.stopPropagation();
    if (printing) return;
    setPrinting(true);
    try {
      // El listado llega con carga selectiva (sin tienda, mods ni impuestos),
      // así que para imprimir hay que pedir el detalle completo del ticket.
      const detail = await Get({
        request: `/tickets/${ticket.id}?shop_id=${localStorage.getItem("shop_id")}`,
      });
      printTicket(GenerateTicket(detail, paperSaving));
      toast.success("Ticket enviado a imprimir");
    } catch {
      toast.error("No se ha podido imprimir el ticket");
    } finally {
      setPrinting(false);
    }
  }

  const leadingActions = (
    <LeadingActions>
      <SwipeAction onClick={onPrint}>
        <div className="flex h-full w-20 flex-col items-center justify-center gap-1 bg-blue-500 text-white">
          <Icon icon="mdi:printer" className="text-2xl" />
          <span className="text-xs font-semibold">Imprimir</span>
        </div>
      </SwipeAction>
    </LeadingActions>
  );

  const trailingActions = canReturn ? (
    <TrailingActions>
      <SwipeAction onClick={onReturn}>
        <div className="flex h-full w-20 flex-col items-center justify-center gap-1 bg-red-500 text-white">
          <Icon icon="mdi:undo-variant" className="text-2xl" />
          <span className="text-xs font-semibold">Devolver</span>
        </div>
      </SwipeAction>
    </TrailingActions>
  ) : undefined;

  return (
    <SwipeableListItem
      className="w-full"
      leadingActions={leadingActions}
      trailingActions={trailingActions}
    >
      <article
        className={`w-full border-b border-neutral-300 transition-colors ${
          selected
            ? "bg-blue-50/70 shadow-[inset_3px_0_0_#2563eb]"
            : isReturned
              ? "bg-neutral-200/40"
              : "bg-transparent hover:bg-white/40"
        }`}
      >
        <div ref={animateRef}>
          <div className="flex min-h-24 items-center px-1 sm:px-2">
            {canReturn && onSelect && (
              <button
                type="button"
                role="checkbox"
                aria-checked={selected}
                aria-label={`Seleccionar ticket ${ticket.reference}`}
                onClick={() => onSelect(ticket.id)}
                className="mr-1 flex h-12 w-12 shrink-0 touch-manipulation items-center justify-center rounded-full text-transparent transition-colors hover:bg-white/70 active:scale-95"
              >
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full border-2 transition-colors ${
                    selected
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-neutral-300 bg-transparent"
                  }`}
                >
                  <Icon icon="mdi:check" className="text-sm" />
                </span>
              </button>
            )}

            <button
              type="button"
              onClick={() => setOpen((current) => !current)}
              aria-expanded={open}
              className="flex min-w-0 flex-1 touch-manipulation items-center gap-3 py-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500/20 active:scale-[0.995]"
            >
              <span className="min-w-0 flex-1">
                <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="truncate text-base font-semibold text-neutral-900">
                    #{ticket.register_id}/{ticket.reference}
                  </span>
                  {Boolean(ticket.returned) && (
                    <span className="text-xs font-medium text-red-600">
                      Devuelto
                    </span>
                  )}
                  {Boolean(ticket.is_returned) && (
                    <span className="text-xs font-medium text-amber-600">
                      Devolución
                    </span>
                  )}
                </span>
                <span className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-500 sm:text-sm">
                  <span>{dayjs(ticket.created_at).format("DD/MM/YYYY · HH:mm")}</span>
                  {ticket.pay_method && (
                    <span className="inline-flex min-w-0 items-center gap-1.5">
                      <Icon
                        icon={getPaymentIcon(ticket.pay_method)}
                        className="text-base text-neutral-400"
                      />
                      <span className="truncate">{ticket.pay_method}</span>
                    </span>
                  )}
                </span>
              </span>

              <span className="shrink-0 text-right text-base font-semibold text-neutral-900 sm:text-lg">
                {formatCurrency(ticket.total)}
              </span>

              <Icon
                icon={open ? "mdi:chevron-up" : "mdi:chevron-down"}
                className="shrink-0 text-xl text-neutral-400"
              />
            </button>

            <button
              type="button"
              onClick={onPrint}
              disabled={printing}
              aria-label={`Imprimir ticket ${ticket.reference}`}
              className="ml-1 flex h-12 w-12 shrink-0 touch-manipulation items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-white/70 hover:text-blue-600 active:scale-95 disabled:opacity-50"
            >
              <Icon
                icon={printing ? "mdi:printer-off" : "mdi:printer-outline"}
                className={`text-xl ${printing ? "animate-pulse" : ""}`}
              />
            </button>
          </div>

          {open && (
            <div className="border-t border-neutral-300 bg-neutral-200/30 px-3 py-4 sm:px-5">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-neutral-700">
                  Productos del ticket
                </h3>
                <span className="text-sm text-neutral-500">
                  {ticket.lines.length}{" "}
                  {ticket.lines.length === 1 ? "línea" : "líneas"}
                </span>
              </div>

              <div className="divide-y divide-neutral-300 border-y border-neutral-300">
                {ticket.lines.map((product, index) => (
                  <div
                    key={product.id || index}
                    className="flex items-start justify-between gap-4 py-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start gap-3">
                        <span className="w-8 shrink-0 text-sm font-medium text-blue-600">
                          ×{product.quantity}
                        </span>
                        <p className="min-w-0 font-medium leading-5 text-neutral-800">
                          {product.products?.name || "Producto"}
                        </p>
                      </div>
                      {product?.mods?.length > 0 && (
                        <div className="mt-1 space-y-0.5 pl-11 text-xs text-neutral-500">
                          {product.mods.map((mod, modIndex) => (
                            <p key={mod.id || modIndex}>
                              {mod.mod.defaultEnabled ? "Sin " : "Con "}
                              {mod.mod.name}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="shrink-0 text-right text-sm text-neutral-500">
                      <div>{formatCurrency(product.custom_price)} / ud.</div>
                      <div className="mt-1 font-semibold text-neutral-900">
                        {formatCurrency(
                          Number(product.custom_price) * product.quantity,
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                {canReturn && (
                  <Button
                    type="button"
                    onClick={onReturn}
                    variant="outline"
                    loading={returning}
                    className="h-12 border-red-200 px-5 text-base text-red-600 hover:bg-red-50 hover:text-red-700"
                  >
                    {!returning && (
                      <Icon icon="mdi:undo-variant" className="mr-2 text-xl" />
                    )}
                    Devolver
                  </Button>
                )}
                <Button
                  type="button"
                  onClick={onPrint}
                  disabled={printing}
                  loading={printing}
                  variant="outline"
                  className="h-12 px-5 text-base"
                >
                  {!printing && (
                    <Icon icon="mdi:printer-outline" className="mr-2 text-xl" />
                  )}
                  {printing ? "Preparando…" : "Imprimir ticket"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </article>
    </SwipeableListItem>
  );
}
